import { CACHE_KEY_LIST_INCIDENTS } from '@/modules/incidents/contants/incidents';
import { CACHE_KEY_LIST_LABDATA_INCIDENTS } from '@/modules/incidents/contants/labDataIncidents';
import { CACHE_KEY_LIST_MEASUREMENTS_INCIDENTS } from '@/modules/incidents/contants/measurementesIncidents';
import { CACHE_KEY_LIST_OTHERS_INCIDENTS } from '@/modules/incidents/contants/othersIncidents';
import { InFilter, OperatorFilter, TermFilter } from '@dofleini/query-builder';
import moment from 'moment';

export const createFilter = (field, values, objectId = true) => {
  const value = values?.[field];
  const filter = { field, value, objectId };
  if (Array.isArray(value))
    return value?.length ? new InFilter(filter).toQuery() : undefined;

  if (value)
    return new TermFilter(filter).toQuery();
};
export const createCustomFilter = (field, values, objectId) => (
  createFilter(field, { [field]: values}, objectId)
);

export const createDateFilter = (field, values) => {
  const dates = values?.[field];
  if (dates?.length) {
    return ({
      field,
      type: 'RANGE',
      from: moment(dates?.[0])?.utc().startOf('date').toISOString(),
      to: moment(dates?.[1])?.utc().endOf('date').toISOString()
    });
  }
};

const extractStatus = (values) => {
  const status = values?.status;
  if(Array.isArray(status)){
    return status;
  }
  return status ? [status] : [];
};

export const buildAuditIncidenceFilters = (values) => {
  let clientsFilter = createFilter('establishment', values);
  let servicesFilter = createFilter('service', values);
  let countriesFilter = createFilter('country', values);
  let structuresFilter = createFilter('structure', values);
  let dateRangeFilter = createDateFilter('auditDate', values);
  let priorityFilter = createFilter('priority', values, false);
  let riskLevelFilter = createFilter('riskLevel', values, false);
  let responsibleFilter = createFilter('responsible', values);
  let categorizationFilter = createFilter('categorization', values, false);
  let statusFilter;
  const statusValue = extractStatus(values);
  if (statusValue?.length) {
    const hasResolved = statusValue?.includes('resolved');
    const hasSolvedInPlace = statusValue?.includes('solvedInPlace');
    const rest = statusValue?.filter(s => s !== 'solvedInPlace');
    const inFilter = new InFilter({ field: 'status', value: rest }).toQuery();
    if (hasSolvedInPlace && !hasResolved) {
      const solvedInPlace = new TermFilter({ field: 'solvedInPlace', value: true }).toQuery();
      statusFilter = rest?.length ? new OperatorFilter({
        type: 'OR',
        filters: [
          solvedInPlace,
          inFilter
        ]
      }).toQuery() : solvedInPlace;
    } else if (hasResolved && !hasSolvedInPlace) {
      statusFilter = new OperatorFilter({
        type: 'AND',
        filters: [
          new TermFilter({ field: 'solvedInPlace', value: { $ne: true } }),
          inFilter
        ]
      });
    }
    else {
      statusFilter = inFilter;
    }
  }

  const filters = [
    statusFilter,
    clientsFilter,
    countriesFilter,
    servicesFilter,
    dateRangeFilter,
    priorityFilter,
    riskLevelFilter,
    responsibleFilter,
    structuresFilter,
    categorizationFilter,
  ].filter(v => v);

  if (filters.length)
    return new OperatorFilter({ type: 'AND', filters }).toQuery();
};

export const buildMeasurementIncidenceFilters = (values) => {
  let clientsFilter = createFilter('establishment', values);
  let facilityFilter = createFilter('facility', values);
  let statusFilter = createFilter('status', values, false);
  let dateRangeFilter = createDateFilter('openDate', values);
  let priorityFilter = createFilter('priority', values, false);
  let areaTypeFilter = createFilter('areaType', values);
  let responsibleFilter = createFilter('responsible', values);

  const filters = [
    statusFilter,
    clientsFilter,
    facilityFilter,
    dateRangeFilter,
    priorityFilter,
    areaTypeFilter,
    responsibleFilter
  ].filter(v => v);

  filters.push(new TermFilter({ field: 'type', value: 'register' }).toQuery());

  return new OperatorFilter({ type: 'AND', filters }).toQuery();
};

export const buildLabDataIncidenceFilters = (values) => {
  let clientsFilter = createFilter('establishment', { establishment: values?.establishment });
  let statusFilter = createFilter('status', { status: values?.status?.map(s => s?.toUpperCase()) }, false);
  let dateRangeFilter = createDateFilter('createdAt', values);
  let responsibleFilter = createFilter('responsible', values);
  let categoryFilter = createFilter('category', values);
  let sampleFilter = createFilter('sampleType', values);

  const filterArray = [
    statusFilter,
    clientsFilter,
    dateRangeFilter,
    responsibleFilter,
    categoryFilter,
    sampleFilter,
  ].filter(v => v);

  if (filterArray.length)
    return new OperatorFilter({ type: 'AND', filters: filterArray }).toQuery();
};

export const buildOtherIncidenceFilters = (values) => {
  let clientsFilter = createFilter('establishment', values);
  let facilityFilter = createFilter('facility', values);
  let statusFilter = createFilter('status', values, false);
  let dateRangeFilter = createDateFilter('openDate', values);
  let priorityFilter = createFilter('priority', values, false);
  let areaTypeFilter = createFilter('areaType', values);
  let responsibleFilter = createFilter('responsible', values);

  const filterArray = [
    statusFilter,
    clientsFilter,
    facilityFilter,
    dateRangeFilter,
    priorityFilter,
    areaTypeFilter,
    responsibleFilter,
  ].filter(v => v);

  filterArray.push(new TermFilter({ field: 'type', value: 'other' }).toQuery());

  return new OperatorFilter({ type: 'AND', filters: filterArray }).toQuery();
};

export const buildIncidentFilters = {
  [CACHE_KEY_LIST_INCIDENTS]: buildAuditIncidenceFilters,
  [CACHE_KEY_LIST_MEASUREMENTS_INCIDENTS]: buildMeasurementIncidenceFilters,
  [CACHE_KEY_LIST_LABDATA_INCIDENTS]: buildLabDataIncidenceFilters,
  [CACHE_KEY_LIST_OTHERS_INCIDENTS]: buildOtherIncidenceFilters,
};

const clearLocalStorage = (key) => window.localStorage.setItem(`${key}_filters`, null);
export const clearLocalStorageIncidentFilters = () => {
  [
    CACHE_KEY_LIST_INCIDENTS,
    CACHE_KEY_LIST_MEASUREMENTS_INCIDENTS,
    CACHE_KEY_LIST_LABDATA_INCIDENTS,
    CACHE_KEY_LIST_OTHERS_INCIDENTS,
  ].map(key => clearLocalStorage(key));
};

export const hasServiceFilter = (filters) => {
  return (
    filters?.filters?.some(filter => filter.field === 'service')
    || filters?.field === 'service'
  );
};
